
import { AxiosResponse } from 'axios';
import { ChangePasswordFormInterface } from './../interfaces/changePasswordForm';
import AuthorizedApiClient from 'modules/auth/models/authorizedApiClient';
import { AppResponse } from '@/modules/core/interfaces/appResponse';
import { PlaceholderInterface } from '../interfaces/placeholder';
import { CreateFactFindTemplateInterface, FactFindMultiResponseInterface, FactFindRequestInterface, FactFindResponseInterface, FactFindTemplateInterface, PresetFactFindCategoryInterface, PresetFactFindQuestionInterface } from '../interfaces/factFind';

export class FactFindApi {
    public async triggerPreview(templateId: number): Promise<AppResponse<boolean>> {
        const response: AxiosResponse<AppResponse<boolean>> =
            await AuthorizedApiClient.post(`/fact-find/trigger-preview/${templateId}`);

        if (response) {
            const appResponse: AppResponse<boolean> = response.data;

            return appResponse;
        }
        return {
            message: 'Failed to trigger fact find preview',
            success: false,
            data: false,
        };
    }
    public async createUpdateTemplate(template: CreateFactFindTemplateInterface): Promise<AppResponse<FactFindTemplateInterface | null>> {
        const response: AxiosResponse<AppResponse<FactFindTemplateInterface>> =
            await AuthorizedApiClient.post('/fact-find/templates', template);

        if (response) {
            const appResponse: AppResponse<FactFindTemplateInterface> = response.data;

            return appResponse;
        }
        return {
            message: 'Failed to create fact find template',
            success: false,
            data: null,
        };
    }

    public async getTemplatesByCompanyId(companyId: number): Promise<AppResponse<FactFindTemplateInterface[]>> {
        const response: AxiosResponse<AppResponse<FactFindTemplateInterface[]>> = 
            await AuthorizedApiClient.get(`/fact-find/templates/${companyId}`);

        if (response) {
            const appResponse: AppResponse<FactFindTemplateInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch fact find template',
                success: false,
                data: [],
            };
        }
    }

    public async getAllPresetCategories(): Promise<AppResponse<PresetFactFindCategoryInterface[]>> {
        const response: AxiosResponse<AppResponse<PresetFactFindCategoryInterface[]>> = 
            await AuthorizedApiClient.get('/fact-find/preset-categories');

        if (response) {
            const appResponse: AppResponse<PresetFactFindCategoryInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch preset categories',
                success: false,
                data: [],
            };
        }
    }

    public async getAllPresetQuestions(): Promise<AppResponse<PresetFactFindQuestionInterface[]>> {
        const response: AxiosResponse<AppResponse<PresetFactFindQuestionInterface[]>> = 
            await AuthorizedApiClient.get('/fact-find/preset-questions');

        if (response) {
            const appResponse: AppResponse<PresetFactFindQuestionInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch preset questions',
                success: false,
                data: [],
            };
        }
    }

    public async getProgress(clientId: number, factFindRequestId?: number): Promise<AppResponse<number | null>> {
        const params = factFindRequestId ? { factFindRequestId } : undefined;
        const response: AxiosResponse<AppResponse<number>> =
            await AuthorizedApiClient.get(`/fact-find/response/get-progress/${clientId}`, { params });

        if (response) {
            const appResponse: AppResponse<number> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch fact find progress',
                success: false,
                data: null,
            }
        }
    }

    /**
     * Lists every active fact find request for a client (across both applicants and
     * however many concurrent templates they have — e.g. a mortgage fact find AND a
     * health questionnaire), each with its own progress.
     */
    public async getClientFactFindRequests(clientId: number): Promise<AppResponse<FactFindRequestInterface[]>> {
        const response: AxiosResponse<AppResponse<FactFindRequestInterface[]>> =
            await AuthorizedApiClient.get(`/fact-find/requests/${clientId}`);

        if (response) {
            const appResponse: AppResponse<FactFindRequestInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch fact find requests',
                success: false,
                data: [],
            };
        }
    }

    public async getPresetQuestionsByCategoryId(categoryId: number): Promise<AppResponse<PresetFactFindQuestionInterface[]>> {
        const response: AxiosResponse<AppResponse<PresetFactFindQuestionInterface[]>> = 
            await AuthorizedApiClient.get(`/fact-find/preset-categories/${categoryId}`);

        if (response) {
            const appResponse: AppResponse<PresetFactFindQuestionInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch preset questions',
                success: false,
                data: [],
            };
        }
    }

    public async getResponseForClient(clientId: number, factFindRequestId?: number) {
        const params = factFindRequestId ? { factFindRequestId } : undefined;
        const response: AxiosResponse<AppResponse<FactFindMultiResponseInterface[]>> =
            await AuthorizedApiClient.get(`/fact-find/response/${clientId}`, { params });

        if (response) {
            const appResponse: AppResponse<FactFindMultiResponseInterface[]> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to fetch fact find response',
                success: false,
                data: [],
            };
        }
    }

    public async toggleFactFindComplete(templateId: number, userId: number, templateType?: "preset" | "custom") {
        const response: AxiosResponse<AppResponse<boolean>> = 
            await AuthorizedApiClient.post(`/fact-find/request/toggle-complete`, 
                { 
                    userId: userId,
                    templateId: templateId,
                    templateType: templateType || 'custom',
                }
            );

        if (response) {
            const appResponse: AppResponse<boolean> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to toggle fact find completion status',
                success: false,
                data: false,
            };
        }
    }

    public async requestFactFind(templateId: number, userId: number, templateType?: "preset" | "custom") {
        const response: AxiosResponse<AppResponse<boolean>> = 
            await AuthorizedApiClient.post(`/fact-find/request`, 
                { 
                    userId: userId,
                    templateId: templateId,
                    templateType: templateType || 'custom',
                }
            );

        if (response) {
            const appResponse: AppResponse<boolean> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to request fact find',
                success: false,
                data: false,
            };
        }
    }

    public async patchResponse(userId: number, responses: FactFindResponseInterface[]) {
        const response: AxiosResponse<AppResponse<boolean>> =
            await AuthorizedApiClient.patch(`/fact-find/response/patch/${userId}`, {
                notifyBroker: false,
                responses: responses,
            });

        if (response) {
            const appResponse: AppResponse<boolean> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to update fact find',
                success: false,
                data: false,
            }
        }

    }

    public async deleteTemplate(templateId: number) {
        const response: AxiosResponse<AppResponse<boolean>> =
                    await AuthorizedApiClient.delete(`/fact-find/template/${templateId}`);

        console.log('response', JSON.stringify(response, null, 2));

        if (response) {
            const appResponse: AppResponse<boolean> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to delete fact find template',
                success: false,
                data: false,
            }
        }

    }

    public async setDefaultTemplate(templateId: number, templateType: string): Promise<AppResponse<FactFindTemplateInterface | null>> {
        const response: AxiosResponse<AppResponse<FactFindTemplateInterface>> =
            await AuthorizedApiClient.post(`/fact-find/template/set-default`, {
                templateId: templateId,
                templateType: templateType,
            });

        if (response) {
            const appResponse: AppResponse<FactFindTemplateInterface> = response.data;

            return appResponse;
        } else {
            return {
                message: 'Failed to set default fact find template',
                success: false,
                data: null,
            };
        }
    }
}